import { t } from 'i18next';
import { UseGridOperations } from '../../hooks/useGridOperations';
import { ReturnTypeofSheetSectionsSelector } from '../../selectors/sheetUiSelector';
import { Area, GridEntity } from '../../types';
import { IContextMenuItem } from './types';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { RiDeleteBackLine, RiDeleteBinLine } from 'react-icons/ri';
import {
  TbColumnInsertLeft,
  TbColumnRemove,
  TbFileBroken,
  TbRowInsertTop,
  TbRowRemove,
} from 'react-icons/tb';
import { indexToColumnLetter } from '../../utils/area/area';
import {
  AiOutlineDeleteColumn,
  AiOutlineDeleteRow,
  AiOutlineInsertRowAbove,
  AiOutlineInsertRowBelow,
  AiOutlineInsertRowLeft,
  AiOutlineInsertRowRight,
} from 'react-icons/ai';
import { VscInsert } from 'react-icons/vsc';
import _ from 'lodash';
import { Direction } from '../../../windows/helpers/adjustWindow';

export {};

/*
    === Cell Menu Actions ===
    Insert >
        Insert rows above
        Insert columns left
        Delete rows
        Delete Columns
        Delete cells
    
    
    Enable File mode
*/

const cellLangKey = 'cells';
const cellLangKeys = (parentLangKey: string) => [
  `${parentLangKey}.${cellLangKey}.insert`,
  `${parentLangKey}.${cellLangKey}.delete`,
];

interface GetCellMenu extends ReturnTypeofSheetSectionsSelector {
  area: Area;
  parentLangKey: string;
}

export const getCellMenu = (props: GetCellMenu) => {
  const { parentLangKey } = props;
  const menus: IContextMenuItem[] = [];

  cellLangKeys(parentLangKey).forEach((langKey) => {
    let nestedMenus: IContextMenuItem[][] | undefined;

    if (langKey.includes('insert')) {
      nestedMenus = [generateInsertMenu(props)];
    } else if (langKey.includes('delete')) {
      nestedMenus = [generateDeleteMenu(props)];
    }

    menus.push({
      title: t(langKey),
      Icon: getMenuIcon(langKey),
      action: getMenuAction(langKey, props),
      nestedMenus,
    });
  });

  return menus;
};

const generateInsertMenu = (props: GetCellMenu) => {
  const { area, parentLangKey } = props;
  return [
    `${parentLangKey}.${cellLangKey}.insert_shift_right`,
    `${parentLangKey}.${cellLangKey}.insert_shift_down`,
    `${parentLangKey}.insert_left`,
    `${parentLangKey}.insert_above`,
  ].map((langKey) => {
    let langParams = {};
    if (langKey.includes('insert_left')) {
      const count = area.end.ci - area.start.ci + 1;
      langParams = {
        count,
        entityName: GridEntity.Column,
      };
    } else if (langKey.includes('insert_above')) {
      const count = area.end.ri - area.start.ri + 1;
      langParams = {
        count,
        entityName: GridEntity.Row,
      };
    }
    return {
      title: t(langKey, langParams),
      Icon: getMenuIcon(langKey),
      action: getMenuAction(langKey, props),
    };
  });
};

const generateDeleteMenu = (props: GetCellMenu) => {
  const { area, parentLangKey } = props;

  const menus = [
    `${parentLangKey}.${cellLangKey}.delete_shift_left`,
    `${parentLangKey}.${cellLangKey}.delete_shift_up`,
  ].map((langKey) => {
    return {
      title: t(langKey),
      Icon: getMenuIcon(langKey),
      action: getMenuAction(langKey, props),
    };
  });

  const deleteMenus = [GridEntity.Column, GridEntity.Row].map((entityName) => {
    const isCol = entityName === GridEntity.Column;
    const alias = isCol ? 'ci' : 'ri';
    const count = area.end[alias] - area.start[alias] + 1;

    const start = isCol
      ? indexToColumnLetter(area.start.ci)
      : area.start.ri + 1; // indexToColumnLetter(area.start.ri);
    const end = isCol ? indexToColumnLetter(area.end.ci) : area.end.ri + 1;

    const langParams = {
      count,
      start,
      end,
      entityName: entityName,
    };

    const key = count > 1 ? `delete_other` : `delete_one`;
    return {
      title: t(`${parentLangKey}.${key}`, langParams),
      Icon: getMenuIcon(key, isCol),
      action: getMenuAction(key, props),
    };
  });

  return menus.concat(deleteMenus);
};

const getMenuIcon = (langKey: string, isCol?: boolean) => {
  switch (true) {
    case langKey.includes('insert'): {
      if (langKey.includes('insert_shift_right')) return AiOutlineInsertRowLeft;
      if (langKey.includes('insert_shift_down')) return AiOutlineInsertRowAbove;
      if (langKey.includes('insert_left')) return TbColumnInsertLeft;
      if (langKey.includes('insert_above')) return TbRowInsertTop;
      return VscInsert;
    }
    case langKey.includes('delete'): {
      if (langKey.includes('delete_other') || langKey.includes('delete_one'))
        return isCol ? TbColumnRemove : TbRowRemove;
      else if (langKey.includes('delete_shift_left'))
        return AiOutlineInsertRowRight;
      else if (langKey.includes('delete_shift_up'))
        return AiOutlineInsertRowBelow;

      return RiDeleteBinLine;
    }
  }
  return TbFileBroken;
};

const getMenuAction = (
  langKey: string,
  props: GetCellMenu,
  isCol?: boolean
) => {
  const { area } = props;
  const columnRange = {
    start: area.start.ci,
    end: area.end.ci,
  };
  const rowRange = {
    start: area.start.ri,
    end: area.end.ri,
  };
  switch (true) {
    case langKey.includes('insert'): {
      if (
        langKey.includes('insert_shift_right') ||
        langKey.includes('insert_shift_down')
      ) {
        const dir = langKey.includes('insert_shift_right')
          ? Direction.Right
          : Direction.Bottom;
        return () => {};
        // performCellOperation({
        //   op: CellOperations.InsertAndShift,
        //   sourceArea: area,
        //   direction: dir,
        // });
      }
      if (langKey.includes('insert_left') || langKey.includes('insert_above')) {
        const isCol = langKey.includes('insert_left');
        return () => {};
        // performCellOperation({
        //   op: CellOperations.Insert,
        //   entityType: isCol ? GridEntity.Column : GridEntity.Row,
        //   range: isCol ? columnRange : rowRange,
        // });
      }
      break;
    }
    case langKey.includes('delete'): {
      if (langKey.includes('delete_other') || langKey.includes('delete_one'))
        return () => {};
      // performCellOperation({
      //   op: langKey.includes('delete')
      //     ? CellOperations.Delete
      //     : CellOperations.Insert,
      //   entityType: isCol ? GridEntity.Column : GridEntity.Row,
      //   range: isCol ? columnRange : rowRange,
      // });
      else if (
        langKey.includes('delete_shift_left') ||
        langKey.includes('delete_shift_up')
      ) {
        const dir = langKey.includes('delete_shift_left')
          ? Direction.Left
          : Direction.Top;
        return () => {};
        // performCellOperation({
        //   op: CellOperations.DeleteAndShift,
        //   sourceArea: area,
        //   direction: dir,
        // });
      }
      break;
    }
  }
  return undefined;
};
